<template>
  <footer class="bg-ink-900 text-white relative overflow-hidden pt-16 pb-10">
    <!-- Decorative brand glow -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-24 right-1/4 w-96 h-96 bg-kettelsen opacity-10 rounded-full blur-3xl"></div>
    </div>

    <div class="container-custom relative z-10">
      <div class="grid gap-10 md:grid-cols-4">
        <!-- Brand -->
        <div class="md:col-span-1">
          <img :src="logoWhite" alt="Kettelsen" class="h-9 w-auto mb-5" />
          <p class="text-white/60 leading-relaxed text-sm">{{ $t('footer.tagline') }}</p>
        </div>

        <!-- Explore -->
        <div>
          <h3 class="text-sm font-bold uppercase tracking-wider text-white/80 border-b border-white/10 pb-2 mb-4">
            {{ $t('footer.exploreTitle') }}
          </h3>
          <ul class="space-y-3 text-sm">
            <li v-for="link in links" :key="link.href">
              <a :href="link.href" class="text-white/60 hover:text-kettelsen transition-colors duration-200">
                {{ $t(link.label) }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div>
          <h3 class="text-sm font-bold uppercase tracking-wider text-white/80 border-b border-white/10 pb-2 mb-4">
            {{ $t('footer.servicesTitle') }}
          </h3>
          <ul class="space-y-3 text-sm">
            <li v-for="(item, i) in services" :key="i">
              <a href="#services" class="text-white/60 hover:text-kettelsen transition-colors duration-200">
                {{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Contact -->
        <div>
          <h3 class="text-sm font-bold uppercase tracking-wider text-white/80 border-b border-white/10 pb-2 mb-4">
            {{ $t('footer.contactTitle') }}
          </h3>
          <address class="not-italic text-white/60 space-y-3 text-sm">
            <p class="flex items-start gap-3">
              <MapPinIcon class="h-5 w-5 text-kettelsen shrink-0 mt-0.5" />
              <span>{{ $t('footer.contact.address') }}</span>
            </p>
            <p class="flex items-center gap-3">
              <PhoneIcon class="h-5 w-5 text-kettelsen shrink-0" />
              <a :href="`tel:${$t('footer.contact.phone').replace(/\s/g, '')}`" class="hover:text-white transition-colors">{{ $t('footer.contact.phone') }}</a>
            </p>
            <p class="flex items-center gap-3">
              <EnvelopeIcon class="h-5 w-5 text-kettelsen shrink-0" />
              <a :href="`mailto:${$t('footer.contact.email')}`" class="hover:text-white transition-colors break-all">{{ $t('footer.contact.email') }}</a>
            </p>
          </address>
          <div class="flex gap-3 mt-5">
            <a href="#" aria-label="LinkedIn" class="w-9 h-9 rounded-full bg-white/10 hover:bg-kettelsen flex items-center justify-center transition-colors">
              <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22.23 0h-20.46c-.97 0-1.77.8-1.77 1.77v20.46c0 .97.8 1.77 1.77 1.77h20.46c.97 0 1.77-.8 1.77-1.77v-20.46c0-.97-.8-1.77-1.77-1.77zm-14.12 20.46h-3.55v-11.36h3.55v11.36zm-1.77-12.88c-1.18 0-2.13-.95-2.13-2.13s.95-2.13 2.13-2.13 2.13.95 2.13 2.13-.95 2.13-2.13 2.13zm14.12 12.88h-3.55v-5.52c0-1.33-.03-3.03-1.85-3.03-1.85 0-2.13 1.44-2.13 2.93v5.62h-3.55v-11.36h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.46v6.19z" /></svg>
            </a>
            <a href="#" aria-label="Instagram" class="w-9 h-9 rounded-full bg-white/10 hover:bg-kettelsen flex items-center justify-center transition-colors">
              <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07zm0 3.68a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zm0 10.16a4 4 0 110-8 4 4 0 010 8zm6.41-10.4a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z" /></svg>
            </a>
            <a href="#" aria-label="Facebook" class="w-9 h-9 rounded-full bg-white/10 hover:bg-kettelsen flex items-center justify-center transition-colors">
              <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" /></svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom bar -->
      <div class="border-t border-white/10 mt-14 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p class="text-white/40 text-sm text-center md:text-left">
          &copy; {{ year }} Kettelsen. {{ $t('footer.rights') }}<br class="md:hidden" />
          <span class="text-white/30">{{ $t('footer.group') }}</span>
        </p>
        <div class="flex flex-wrap justify-center gap-5 text-sm">
          <a href="#" class="text-white/40 hover:text-white transition-colors duration-200">{{ $t('footer.privacy') }}</a>
          <a href="#" class="text-white/40 hover:text-white transition-colors duration-200">{{ $t('footer.terms') }}</a>
          <a href="https://kettelsenconsulting.com" target="_blank" rel="noopener" class="text-white/40 hover:text-white transition-colors duration-200">Kettelsen Consulting</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/vue/24/outline'
import logoWhite from '@/assets/logo-white.png'

export default {
  name: 'FooterBar',
  components: { MapPinIcon, PhoneIcon, EnvelopeIcon },
  setup() {
    const { tm } = useI18n()
    return {
      logoWhite,
      year: new Date().getFullYear(),
      services: computed(() => tm('services.items')),
      links: [
        { href: '#services', label: 'nav.services' },
        { href: '#process', label: 'nav.process' },
        { href: '#why', label: 'nav.why' },
        { href: '#contact', label: 'nav.book' }
      ]
    }
  }
}
</script>
